'use client';
/**
 * Editor-facing API layer: the typed fetch client from `@/lib/api` (contract §5.5, DTOs from
 * @freealbum/shared) plus the multi-step flows the editor needs — presigned upload with progress,
 * optimistic-concurrency save, and export polling.
 */
import { uploadScheduler } from './upload-scheduler';
import type { AlbumDocument, ExportOptions, PhotoInfo } from '@freealbum/document';
import type { ExportJobDto, PresignFile } from '@freealbum/shared';
import {
  ACCEPTED_IMAGE_TYPES,
  ApiError,
  MAX_FILE_BYTES,
  api,
  guessMime,
  uploadToPresignedUrl,
  type ImportItem,
  type ImportSource,
} from '@/lib/api';

export { api, ApiError, ACCEPTED_IMAGE_TYPES, MAX_FILE_BYTES, guessMime };
export { PHOTO_INPUT_ACCEPT } from '@/lib/api';

export type SaveResult =
  | { ok: true; version: number }
  | { ok: false; conflict: { version: number; doc: AlbumDocument } }
  | { ok: false; error: ApiError };

/** PATCH the document with optimistic concurrency. A 409 returns the server's newer state. */
export async function saveDocument(
  albumId: string,
  body: { doc?: AlbumDocument; title?: string; version: number },
): Promise<SaveResult> {
  try {
    const { version } = await api.albums.patch(albumId, body);
    return { ok: true, version };
  } catch (err) {
    if (err instanceof ApiError && err.code === 'VERSION_CONFLICT') {
      const b = err.body;
      if (b?.doc && typeof b.version === 'number') return { ok: false, conflict: { version: b.version, doc: b.doc } };
    }
    return { ok: false, error: err instanceof ApiError ? err : new ApiError(0, null, 'NETWORK') };
  }
}

export type UploadKind = 'album' | 'contribute';

export interface UploadCallbacks {
  /** Runs for every file before presigning, including files waiting for a free upload slot. */
  onQueued?: (localId: string, file: File) => void;
  /** Link every queued tile before polling can discover its server row. */
  onPrepared?: (localId: string, photoId: string) => void;
  onStart?: (localId: string, file: File, photoId: string) => void;
  onProgress?: (localId: string, fraction: number) => void;
  onDone?: (localId: string, photo: PhotoInfo) => void;
  onError?: (localId: string, message: string, file: File) => void;
}

export interface UploadTarget {
  kind: UploadKind;
  /** album id, or the contributor token when kind === 'contribute' */
  id: string;
  contributorName?: string;
  resumePhotoId?: string;
}

export interface RejectedFile {
  file: File;
  reason: 'size' | 'type';
}

export function partitionFiles(files: File[]): { accepted: File[]; rejected: RejectedFile[] } {
  const accepted: File[] = [];
  const rejected: RejectedFile[] = [];
  for (const file of files) {
    const mime = guessMime(file);
    if (!ACCEPTED_IMAGE_TYPES.includes(mime)) rejected.push({ file, reason: 'type' });
    else if (file.size > MAX_FILE_BYTES) rejected.push({ file, reason: 'size' });
    else accepted.push(file);
  }
  return { accepted, rejected };
}

let uploadCounter = 0;

/**
 * presign → PUT straight to object storage (with progress) → complete.
 * Files are uploaded with limited concurrency so a 200-photo drop does not open 200 sockets.
 */
export async function uploadPhotos(target: UploadTarget, files: File[], cb: UploadCallbacks = {}): Promise<PhotoInfo[]> {
  if (files.length === 0) return [];
  const localIds = files.map(() => `u${++uploadCounter}`);
  files.forEach((file, index) => cb.onQueued?.(localIds[index]!, file));
  const descriptors: PresignFile[] = files.map((f) => ({ name: f.name, mime: guessMime(f) as PresignFile['mime'], bytes: f.size }));
  const { uploads } = await (async () => {
    try {
      return await (target.kind === 'album'
        ? api.photos.presign(target.id, descriptors, target.resumePhotoId)
        : api.contribute.presign(target.id, target.contributorName ?? '', descriptors));
    } catch (err) {
      files.forEach((file, index) => cb.onError?.(localIds[index]!, err instanceof Error ? err.message : 'presign', file));
      throw err;
    }
  })();

  uploads.forEach((upload, index) => {
    if (localIds[index]) cb.onPrepared?.(localIds[index]!, upload.photoId);
  });

  const results = await Promise.all(files.map((file, index) => uploadScheduler.run(file.size, async () => {
    const upload = uploads[index];
    const localId = localIds[index]!;
    if (!upload) throw new Error('presign');
    cb.onStart?.(localId, file, upload.photoId);
    await uploadToPresignedUrl(upload, file, (fraction) => cb.onProgress?.(localId, fraction));
    const { photo } = target.kind === 'album'
      ? await api.photos.complete(target.id, upload.photoId)
      : await api.contribute.complete(target.id, upload.photoId);
    cb.onDone?.(localId, photo);
    return photo;
  }).catch((error: unknown) => {
    cb.onError?.(localIds[index]!, error instanceof Error ? error.message : 'upload', file);
    return null;
  })));

  return results.filter((photo): photo is PhotoInfo => photo !== null);
}

export async function importFromGoogle(albumId: string, source: ImportSource, items: ImportItem[]): Promise<PhotoInfo[]> {
  const { photos } = await api.photos.import(albumId, source, items);
  return photos;
}

export const EXPORT_POLL_MS = 1500;

/** Polls `GET /api/exports/:id` until the job settles. Returns a stop function. */
export function pollExport(jobId: string, onUpdate: (job: ExportJobDto) => void, onError?: (e: unknown) => void): () => void {
  let stopped = false;
  let timer: ReturnType<typeof setTimeout> | null = null;
  const tick = async () => {
    if (stopped) return;
    try {
      const { job } = await api.exports.get(jobId);
      if (stopped) return;
      onUpdate(job);
      if (job.status === 'done' || job.status === 'failed' || job.status === 'cancelled') return;
    } catch (err) {
      if (stopped) return;
      onError?.(err);
    }
    timer = setTimeout(tick, EXPORT_POLL_MS);
  };
  void tick();
  return () => {
    stopped = true;
    if (timer) clearTimeout(timer);
  };
}

export async function createExport(albumId: string, options: ExportOptions, version?: number): Promise<ExportJobDto> {
  const { job } = await api.exports.create(albumId, options, version);
  return job;
}
